import type {
  BehaviorDomain, HabitCategory, Pace, PlanType,
} from '../core/types.js';

export type GoalStyle =
  | 'wait_longer'
  | 'do_it_less'
  | 'inside_hours'
  | 'spend_less'
  | 'use_less_each'
  | 'shorten_sessions'
  | 'avoid_impulse_purchases'
  | 'combine'
  | 'unsure';

export interface GoalStyleOption {
  value: GoalStyle;
  label: string;
  planType: PlanType;
}

export interface FirstWeekTarget {
  pace: Pace;
  label: string;
  /** Plain-language description shown to the user. */
  description: string;
  /** Interval target in minutes (used when planType=interval). */
  intervalMinutes?: number;
  /** Quota numbers (used when planType=quota). */
  quotaPercentReduction?: number;
  /** Window plan first-use minutes since midnight (used when planType=window). */
  windowFirstUseMinutes?: number;
}

export type QuickActionKind =
  | 'log_used'
  | 'log_waited'
  | 'start_urge'
  | 'log_purchase'
  | 'log_skipped_purchase'
  | 'log_skipped_deposit'
  | 'open_resources';

export interface QuickAction {
  id: string;
  kind: QuickActionKind;
  label: string;
  /** Optional variant for hero button styling. */
  variant?: 'primary' | 'default' | 'ghost' | 'urge';
}

export interface DomainTemplate {
  domain: BehaviorDomain;
  category: HabitCategory;
  displayName: string;
  examples: string[];
  goalStyles: GoalStyleOption[];
  /** Common patterns to choose from in step 3. */
  patternOptions: { value: string; label: string; suggestedPlanType: PlanType }[];
  /** Plain-language first-week targets per planType + pace. */
  firstWeekTargets: Record<PlanType, FirstWeekTarget[]>;
  /** Common triggers shown as chips. */
  commonTriggers: string[];
  /** Default unit (singular, plural). */
  defaultUnit: [string, string];
  /** Safety note shown on confirmation step. */
  safetyNote?: string;
  /** Domain-specific Now hero secondary actions, up to 3. */
  quickActions?: QuickAction[];
}

const INTERVAL_TARGETS: FirstWeekTarget[] = [
  { pace: 'gentle',    label: 'Gentle',    description: 'Wait 3 minutes before each use.',  intervalMinutes: 3 },
  { pace: 'steady',    label: 'Standard',  description: 'Wait 5 minutes before each use.',  intervalMinutes: 5 },
  { pace: 'ambitious', label: 'Ambitious', description: 'Wait 10 minutes before each use.', intervalMinutes: 10 },
];

const QUOTA_TARGETS: FirstWeekTarget[] = [
  { pace: 'gentle',    label: 'Gentle',    description: 'Reduce by about 5–10%.',  quotaPercentReduction: 8 },
  { pace: 'steady',    label: 'Standard',  description: 'Reduce by about 10–20%.', quotaPercentReduction: 15 },
  { pace: 'ambitious', label: 'Ambitious', description: 'Reduce by about 20–30%.', quotaPercentReduction: 25 },
];

const WINDOW_TARGETS: FirstWeekTarget[] = [
  { pace: 'gentle',    label: 'Gentle',    description: 'Hold off until 11 AM most days.',     windowFirstUseMinutes: 11 * 60 },
  { pace: 'steady',    label: 'Standard',  description: 'Hold off until noon most days.',      windowFirstUseMinutes: 12 * 60 },
  { pace: 'ambitious', label: 'Ambitious', description: 'Hold off until 3 PM most days.',      windowFirstUseMinutes: 15 * 60 },
];

const DEFAULT_TARGETS: Record<PlanType, FirstWeekTarget[]> = {
  interval: INTERVAL_TARGETS,
  quota:    QUOTA_TARGETS,
  window:   WINDOW_TARGETS,
  hybrid:   INTERVAL_TARGETS,
};

const DEFAULT_GOAL_STYLES: GoalStyleOption[] = [
  { value: 'wait_longer',  label: 'Wait longer before I do it', planType: 'interval' },
  { value: 'do_it_less',   label: 'Do it fewer times',           planType: 'quota' },
  { value: 'inside_hours', label: 'Keep it inside certain hours', planType: 'window' },
  { value: 'unsure',       label: "I'm not sure — choose for me", planType: 'interval' },
];

export const DOMAIN_TEMPLATES: DomainTemplate[] = [
  {
    domain: 'nicotine', category: 'smoking',
    displayName: 'Smoking or vaping',
    examples: ['cigarettes, vapes, pouches'],
    goalStyles: DEFAULT_GOAL_STYLES,
    patternOptions: [
      { value: 'many_daily', label: 'Many times throughout the day', suggestedPlanType: 'interval' },
      { value: 'after_meals', label: 'Mostly after meals or breaks', suggestedPlanType: 'window' },
      { value: 'weekly_total', label: "I'd rather watch a weekly total", suggestedPlanType: 'quota' },
      { value: 'unsure', label: "I'm not sure yet", suggestedPlanType: 'interval' },
    ],
    firstWeekTargets: DEFAULT_TARGETS,
    commonTriggers: ['stress', 'after_meals', 'morning', 'driving', 'social', 'boredom', 'late_night'],
    defaultUnit: ['cigarette', 'cigarettes'],
    quickActions: [
      { id: 'log', kind: 'log_used', label: 'Log nicotine' },
      { id: 'waited', kind: 'log_waited', label: 'I waited' },
      { id: 'bought', kind: 'log_purchase', label: 'Log a purchase' },
    ],
  },
  {
    domain: 'alcohol', category: 'alcohol',
    displayName: 'Alcohol',
    examples: ['beer, wine, mixed drinks'],
    goalStyles: [
      { value: 'do_it_less',  label: 'Fewer drinks per week',        planType: 'quota' },
      { value: 'inside_hours', label: 'Avoid certain days or hours', planType: 'window' },
      { value: 'wait_longer', label: 'Delay the first drink',        planType: 'interval' },
      { value: 'unsure',      label: "I'm not sure — choose for me", planType: 'quota' },
    ],
    patternOptions: [
      { value: 'few_per_week', label: 'A few drinking days per week', suggestedPlanType: 'quota' },
      { value: 'evenings',     label: 'Mostly evenings',              suggestedPlanType: 'window' },
      { value: 'varies',       label: 'It varies a lot',              suggestedPlanType: 'quota' },
      { value: 'unsure',       label: "I'm not sure yet",             suggestedPlanType: 'quota' },
    ],
    firstWeekTargets: DEFAULT_TARGETS,
    commonTriggers: ['stress', 'social', 'after_work', 'loneliness', 'celebration', 'sleep'],
    defaultUnit: ['drink', 'drinks'],
    safetyNote: 'If you drink heavily or daily, withdrawal can be dangerous. Please consult a clinician before stopping abruptly.',
    quickActions: [
      { id: 'log', kind: 'log_used', label: 'Log drink' },
      { id: 'waited', kind: 'log_waited', label: 'I skipped one' },
      { id: 'safety', kind: 'open_resources', label: 'Safety' },
    ],
  },
  {
    domain: 'cannabis', category: 'cannabis',
    displayName: 'Cannabis',
    examples: ['sessions, edibles, vape'],
    goalStyles: [
      { value: 'wait_longer',  label: 'Delay first use of the day', planType: 'window' },
      { value: 'do_it_less',   label: 'Use fewer sessions',         planType: 'quota' },
      { value: 'shorten_sessions', label: 'Shorten sessions',       planType: 'interval' },
      { value: 'unsure',       label: "I'm not sure",               planType: 'window' },
    ],
    patternOptions: [
      { value: 'wake_and_bake', label: 'Often in the morning', suggestedPlanType: 'window' },
      { value: 'evenings',      label: 'Mostly evenings',      suggestedPlanType: 'window' },
      { value: 'many_daily',    label: 'Many small sessions',  suggestedPlanType: 'interval' },
      { value: 'unsure',        label: "I'm not sure",         suggestedPlanType: 'window' },
    ],
    firstWeekTargets: DEFAULT_TARGETS,
    commonTriggers: ['sleep', 'boredom', 'anxiety', 'pain', 'social', 'after_work'],
    defaultUnit: ['session', 'sessions'],
    quickActions: [
      { id: 'log', kind: 'log_used', label: 'Log session' },
      { id: 'waited', kind: 'log_waited', label: 'I waited' },
    ],
  },
  {
    domain: 'caffeine', category: 'caffeine',
    displayName: 'Caffeine',
    examples: ['coffee, energy drinks, tea'],
    goalStyles: [
      { value: 'wait_longer',  label: 'Delay first caffeine',          planType: 'window' },
      { value: 'do_it_less',   label: 'Fewer cups per day',            planType: 'quota' },
      { value: 'inside_hours', label: 'No caffeine after a set time',  planType: 'window' },
      { value: 'unsure',       label: "I'm not sure",                  planType: 'quota' },
    ],
    patternOptions: [
      { value: 'morning_only', label: 'Mostly mornings', suggestedPlanType: 'quota' },
      { value: 'all_day',      label: 'All day',         suggestedPlanType: 'quota' },
      { value: 'energy_drinks', label: 'Mostly energy drinks', suggestedPlanType: 'quota' },
      { value: 'unsure',        label: "I'm not sure",   suggestedPlanType: 'quota' },
    ],
    firstWeekTargets: DEFAULT_TARGETS,
    commonTriggers: ['tired', 'work_break', 'after_meals'],
    defaultUnit: ['cup', 'cups'],
    quickActions: [
      { id: 'log', kind: 'log_used', label: 'Log caffeine' },
      { id: 'waited', kind: 'log_waited', label: 'I waited' },
    ],
  },
  {
    domain: 'digital', category: 'social_media',
    displayName: 'Social media',
    examples: ['Instagram, TikTok, Twitter'],
    goalStyles: [
      { value: 'wait_longer',     label: 'Wait before opening the app', planType: 'interval' },
      { value: 'shorten_sessions', label: 'Shorten each session',       planType: 'quota' },
      { value: 'inside_hours',    label: 'Avoid late-night scrolling',  planType: 'window' },
      { value: 'unsure',          label: "I'm not sure",                planType: 'interval' },
    ],
    patternOptions: [
      { value: 'many_opens', label: 'Many short opens', suggestedPlanType: 'interval' },
      { value: 'long_sessions', label: 'Long sessions', suggestedPlanType: 'quota' },
      { value: 'late_night', label: 'Mostly late at night', suggestedPlanType: 'window' },
      { value: 'unsure',     label: "I'm not sure",       suggestedPlanType: 'interval' },
    ],
    firstWeekTargets: DEFAULT_TARGETS,
    commonTriggers: ['boredom', 'anxiety', 'waiting', 'late_night'],
    defaultUnit: ['session', 'sessions'],
    quickActions: [
      { id: 'log', kind: 'log_used', label: 'Start session' },
      { id: 'waited', kind: 'log_waited', label: 'I closed it early' },
    ],
  },
  {
    domain: 'digital', category: 'gaming',
    displayName: 'Gaming',
    examples: ['PC, console, mobile'],
    goalStyles: [
      { value: 'shorten_sessions', label: 'Shorten sessions',         planType: 'quota' },
      { value: 'inside_hours',    label: 'Avoid late-night play',     planType: 'window' },
      { value: 'do_it_less',      label: 'Play fewer days per week',  planType: 'quota' },
      { value: 'unsure',          label: "I'm not sure",              planType: 'quota' },
    ],
    patternOptions: [
      { value: 'evenings', label: 'Mostly evenings', suggestedPlanType: 'window' },
      { value: 'long_sessions', label: 'Long sessions', suggestedPlanType: 'quota' },
      { value: 'late_night', label: 'Late night runs', suggestedPlanType: 'window' },
      { value: 'unsure', label: "I'm not sure", suggestedPlanType: 'quota' },
    ],
    firstWeekTargets: DEFAULT_TARGETS,
    commonTriggers: ['boredom', 'stress', 'avoidance'],
    defaultUnit: ['session', 'sessions'],
    quickActions: [
      { id: 'log', kind: 'log_used', label: 'Start session' },
      { id: 'end_early', kind: 'log_waited', label: 'I ended early' },
    ],
  },
  {
    domain: 'gambling', category: 'gambling',
    displayName: 'Gambling',
    examples: ['sports betting, slots, casino'],
    goalStyles: [
      { value: 'wait_longer',  label: 'Delay each bet impulse',          planType: 'interval' },
      { value: 'spend_less',   label: 'Lower the spending cap',          planType: 'quota' },
      { value: 'avoid_impulse_purchases', label: 'Avoid deposits/chases', planType: 'interval' },
      { value: 'unsure',       label: "I'm not sure",                    planType: 'interval' },
    ],
    patternOptions: [
      { value: 'urges',     label: 'Frequent urges',  suggestedPlanType: 'interval' },
      { value: 'deposits',  label: 'Recurring deposits', suggestedPlanType: 'interval' },
      { value: 'sessions',  label: 'Long sessions',     suggestedPlanType: 'quota' },
      { value: 'unsure',    label: "I'm not sure",      suggestedPlanType: 'interval' },
    ],
    firstWeekTargets: DEFAULT_TARGETS,
    commonTriggers: ['stress', 'loss_chasing', 'boredom', 'social', 'late_night'],
    defaultUnit: ['session', 'sessions'],
    safetyNote: 'If gambling has become unmanageable, please reach out to a support line (1-800-GAMBLER in the US). Wean does not replace clinical care.',
    quickActions: [
      { id: 'delayed', kind: 'log_waited', label: 'I delayed a bet' },
      { id: 'skipped', kind: 'log_skipped_deposit', label: 'I avoided a deposit' },
      { id: 'safety', kind: 'open_resources', label: 'Get help' },
    ],
  },
  {
    domain: 'other', category: 'porn',
    displayName: 'Porn',
    examples: ['urges, sessions'],
    goalStyles: [
      { value: 'wait_longer',     label: 'Delay the urge',           planType: 'interval' },
      { value: 'shorten_sessions', label: 'Shorten sessions',        planType: 'quota' },
      { value: 'inside_hours',    label: 'Avoid certain times',      planType: 'window' },
      { value: 'unsure',          label: "I'm not sure",             planType: 'interval' },
    ],
    patternOptions: [
      { value: 'late_night', label: 'Mostly late night', suggestedPlanType: 'window' },
      { value: 'stress',     label: 'When stressed',     suggestedPlanType: 'interval' },
      { value: 'long_sessions', label: 'Long sessions',  suggestedPlanType: 'quota' },
      { value: 'unsure',     label: "I'm not sure",      suggestedPlanType: 'interval' },
    ],
    firstWeekTargets: DEFAULT_TARGETS,
    commonTriggers: ['late_night', 'stress', 'loneliness', 'boredom'],
    defaultUnit: ['session', 'sessions'],
    quickActions: [
      { id: 'log', kind: 'log_used', label: 'Log session' },
      { id: 'waited', kind: 'log_waited', label: 'I waited it out' },
    ],
  },
  {
    domain: 'shopping', category: 'shopping',
    displayName: 'Shopping',
    examples: ['online impulse buys'],
    goalStyles: [
      { value: 'avoid_impulse_purchases', label: 'Delay non-essential buys', planType: 'interval' },
      { value: 'spend_less', label: 'Lower spending cap', planType: 'quota' },
      { value: 'shorten_sessions', label: 'Reduce browsing time', planType: 'quota' },
      { value: 'unsure',     label: "I'm not sure", planType: 'interval' },
    ],
    patternOptions: [
      { value: 'impulse', label: 'Impulse buys', suggestedPlanType: 'interval' },
      { value: 'subscriptions', label: 'Too many subscriptions', suggestedPlanType: 'quota' },
      { value: 'browsing', label: 'Endless browsing', suggestedPlanType: 'quota' },
      { value: 'unsure',  label: "I'm not sure", suggestedPlanType: 'interval' },
    ],
    firstWeekTargets: DEFAULT_TARGETS,
    commonTriggers: ['stress', 'boredom', 'sale', 'social'],
    defaultUnit: ['purchase', 'purchases'],
    quickActions: [
      { id: 'bought', kind: 'log_purchase', label: 'I bought it' },
      { id: 'skipped', kind: 'log_skipped_purchase', label: 'I skipped it' },
    ],
  },
  {
    domain: 'food', category: 'snacking',
    displayName: 'Snacking',
    examples: ['late-night, sugar, mindless'],
    goalStyles: [
      { value: 'wait_longer',     label: 'Delay cravings', planType: 'interval' },
      { value: 'inside_hours',    label: 'Avoid late-night snacking', planType: 'window' },
      { value: 'do_it_less',      label: 'Fewer episodes per day', planType: 'quota' },
      { value: 'unsure',          label: "I'm not sure", planType: 'interval' },
    ],
    patternOptions: [
      { value: 'late_night', label: 'Mostly late at night', suggestedPlanType: 'window' },
      { value: 'stress',     label: 'When stressed', suggestedPlanType: 'interval' },
      { value: 'many_daily', label: 'Many small episodes', suggestedPlanType: 'quota' },
      { value: 'unsure',     label: "I'm not sure", suggestedPlanType: 'interval' },
    ],
    firstWeekTargets: DEFAULT_TARGETS,
    commonTriggers: ['boredom', 'stress', 'late_night', 'tired'],
    defaultUnit: ['snack', 'snacks'],
    safetyNote: 'Wean is not designed to support disordered eating. If you have symptoms of an eating disorder, please reach out to a clinician.',
    quickActions: [
      { id: 'log', kind: 'log_used', label: 'Log a snack' },
      { id: 'waited', kind: 'log_waited', label: 'I waited' },
    ],
  },
  {
    domain: 'other', category: 'other',
    displayName: 'Something else',
    examples: ['private label'],
    goalStyles: DEFAULT_GOAL_STYLES,
    patternOptions: [
      { value: 'many_daily', label: 'Many times a day', suggestedPlanType: 'interval' },
      { value: 'weekly',     label: 'A few times a week', suggestedPlanType: 'quota' },
      { value: 'context',    label: 'At certain times',  suggestedPlanType: 'window' },
      { value: 'unsure',     label: "I'm not sure",      suggestedPlanType: 'interval' },
    ],
    firstWeekTargets: DEFAULT_TARGETS,
    commonTriggers: ['stress', 'boredom', 'habit'],
    defaultUnit: ['use', 'uses'],
  },
];

export function getTemplate(category: HabitCategory): DomainTemplate | undefined {
  return DOMAIN_TEMPLATES.find((t) => t.category === category);
}
